import React, { useState, useCallback, useRef, useEffect } from "react";
import { toast } from "react-hot-toast";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import Success360Service from "../../Services/Success360Service";

export default function EditSuccess360Banner({
  show,
  onHide,
  onSuccess,
  bannerData,
  pageTitle = "Success360 Banner",
}) {
  const [validated, setValidated] = useState(false);
  const imageRef = useRef(null);
  const [editData, setEditData] = useState({
    image: "",
    imageName: "",
    imageType: "",
    currentImagePath: "",
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (bannerData && show) {
      setEditData({
        image: "",
        imageName: "",
        imageType: "",
        currentImagePath: bannerData.imagePath || "",
      });
    }
  }, [bannerData, show]);

  const handleImageInput = useCallback(async (event) => {
    const { files } = event.target;
    const file = files[0];

    if (file) {
      const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];

      if (!allowedTypes.includes(file.type)) {
        toast.error("Please select a valid image file (JPEG, PNG)");
        return;
      }

      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        toast.error("File size must be less than 5MB");
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setEditData((prev) => ({
          ...prev,
          image: reader.result.split(",")[1],
          imageName: file.name,
          imageType: file.type,
        }));
      };
      reader.readAsDataURL(file);
    }
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    setValidated(true);

    if (form.checkValidity() === false) {
      event.stopPropagation();
      return;
    }

    if (!editData.image && !editData.currentImagePath) {
      toast.error("Please select an image file");
      return;
    }

    setIsSaving(true);
    try {
      const loadingMessage = toast.loading("Updating...");

      let response;

      if (editData.image) {
        const formData = new FormData();

        const byteCharacters = atob(editData.image);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const file = new File([byteArray], editData.imageName, {
          type: editData.imageType,
        });

        formData.append("image", file);
        response = await Success360Service.updateSuccess360Banner(
          bannerData.id,
          formData
        );
      } else {
        const updateData = {
          imagePath: editData.currentImagePath,
        };
        response = await Success360Service.updateSuccess360Banner(
          bannerData.id,
          updateData
        );
      }

      if (response.data.success === true) {
        toast.success(response.data.message, { id: loadingMessage });
        setEditData({
          image: "",
          imageName: "",
          imageType: "",
          currentImagePath: "",
        });
        setValidated(false);
        onSuccess && onSuccess();
        onHide();
      } else if (
        response.data.success === false &&
        response.data.response === false
      ) {
        toast.error(response.data.message, { id: loadingMessage });
        setIsSaving(false);
      } else if (
        response.data.success === false &&
        response.data.validationErrors &&
        response.data.validationErrors.length > 0
      ) {
        response.data.validationErrors.map((err) =>
          toast.error(err, { id: loadingMessage })
        );
        setIsSaving(false);
      } else {
        setIsSaving(false);
        toast.error("Something Went Wrong", { id: loadingMessage });
      }
    } catch (error) {
      console.log(error);
      setIsSaving(false);
      toast.error("An error occurred");
    }
  };

  const handleClose = () => {
    setEditData({
      image: "",
      imageName: "",
      imageType: "",
      currentImagePath: "",
    });
    setValidated(false);
    onHide();
  };

  const handleRemoveNewImage = () => {
    setEditData((prev) => ({
      ...prev,
      image: "",
      imageName: "",
      imageType: "",
    }));
  };

  return (
    <Modal show={show} onHide={handleClose} className="mt-5" size="lg">
      <Modal.Header closeButton={!isSaving}>
        <Modal.Title>Edit {pageTitle}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form
          noValidate
          validated={validated}
          onSubmit={handleSubmit}
          className="px-3"
        >
          <Row xs={12} md={12} className="g-3">
            <Col xs={12} md={12}>
              <div className="d-flex flex-column justify-content-center">
                <p className="text1-3 text-light-gray mb-0">Banner Image</p>

                {editData.currentImagePath && !editData.image && (
                  <div className="mt-2">
                    <p className="text-muted small mb-2">Current Image:</p>
                    <div className="d-flex position-relative">
                      <img
                        src={editData.currentImagePath}
                        alt="Current Banner"
                        style={{ maxWidth: "90%", maxHeight: "200px" }}
                      />
                    </div>
                  </div>
                )}

                {editData.image ? (
                  <div className="mt-2">
                    <p className="text-muted small mb-2">New Image:</p>
                    <div className="d-flex position-relative">
                      <img
                        src={`data:${editData.imageType};base64,${editData.image}`}
                        alt="New Upload"
                        style={{ maxWidth: "90%", maxHeight: "200px" }}
                      />
                      <button
                        className="btn btn-link btn-cross"
                        style={{
                          position: "absolute",
                          right: "35px",
                          top: "-13px",
                        }}
                        onClick={handleRemoveNewImage}
                      >
                        <i className="bi bi-x-circle-fill text-danger text2"></i>
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-2">
                    <p className="text-muted small mb-2">
                      {editData.currentImagePath
                        ? "Upload New Image (Optional):"
                        : "Upload Image:"}
                    </p>
                    <div className="text1-4 text-dark1 d-flex align-items-center ms-2">
                      <label
                        htmlFor="importInput_image_edit"
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center">
                          <i className="bi bi-cloud-plus text-dark1 text1-6 d-flex align-items-center justify-content-center me-2"></i>
                          <span className="text1-3">Upload New Image</span>
                        </div>
                      </label>

                      <input
                        id="importInput_image_edit"
                        type="file"
                        className="d-none"
                        ref={imageRef}
                        accept="image/jpeg,image/jpg,image/png"
                        onChange={handleImageInput}
                      />
                    </div>
                    <Form.Control.Feedback type="invalid">
                      Please upload a banner image
                    </Form.Control.Feedback>
                  </div>
                )}
              </div>
            </Col>

            <Modal.Footer className="mt-4">
              <Button
                variant="secondary"
                onClick={handleClose}
                disabled={isSaving}
              >
                Close
              </Button>
              <Button type="submit" variant="success" disabled={isSaving}>
                {isSaving ? "Updating..." : "Update"}
              </Button>
            </Modal.Footer>
          </Row>
        </Form>
      </Modal.Body>
    </Modal>
  );
}
